/*
  Sticky add-to-cart bar for the mobile product page.

  The UI is optimistic: a tap repaints the bar immediately and the network
  write is debounced behind it, so tapping never waits on a round trip. The
  server's count is treated as authoritative only once the shopper has stopped
  tapping, otherwise a slow response would fight newer input.

  All writes go through cart/update.js, which takes ABSOLUTE quantities, adds
  the variant if it is not in the cart yet, and returns the whole cart — so
  add, increment, decrement and remove are one request with no follow-up read.
  (Dawn's own quick-add-bulk uses the same endpoint the same way.)

  Loaded with defer AFTER constants.js and pubsub.js. Both declare their
  globals with bare const/function in classic scripts, so they are in global
  scope but not properties of window — hence the `typeof` guards.
*/
(function () {
  var bar = document.querySelector('[data-pl-sticky-atc]');
  if (!bar) return;

  var addBtn = bar.querySelector('[data-pl-add]');
  var soldOutBtn = bar.querySelector('[data-pl-soldout]');
  var cartBox = bar.querySelector('[data-pl-cart]');
  var countEl = bar.querySelector('[data-pl-count]');
  var priceEl = bar.querySelector('[data-pl-price]');
  var compareEl = bar.querySelector('[data-pl-compare]');
  var miniEl = bar.querySelector('[data-pl-mini]');

  var desired = parseInt(bar.getAttribute('data-in-cart'), 10) || 0;
  var cartTotal = parseInt(bar.getAttribute('data-cart-total'), 10) || 0;
  var flushTimer = null;
  var inFlight = false;

  // An add handed to the product form is outstanding: the POST has been sent
  // but nothing has confirmed it landed yet. While this is set, reconciliation
  // is only allowed to move FORWARD — see reconcile().
  var pendingAdd = false;
  var pendingFloor = 0; // count the optimistic repaint is claiming
  var pendingTotal = 0; // cart item_count before the add, for the bundle case
  var pendingBefore = 0; // count before the add, to undo it without a round trip
  var pendingMini = false; // whether the optimistic repaint added a circle

  // True when an add clearly succeeded but produced line items we cannot count
  // against this variant — a bundle expands into its COMPONENT products, each
  // with its own variant id, so the bundle's own id is never in the cart.
  var opaqueAdded = false;

  var DEBOUNCE_MS = 300;

  function variantId() {
    return parseInt(bar.getAttribute('data-variant-id'), 10);
  }

  function root() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  /* ---------- rendering ---------- */

  function render(count) {
    var n = parseInt(count, 10);
    if (isNaN(n) || n < 0) n = 0;

    bar.setAttribute('data-in-cart', String(n));
    if (countEl) countEl.textContent = String(n);

    var unavailable = bar.hasAttribute('data-unavailable');
    var inCart = n > 0 || opaqueAdded;

    // Stepping a bundle is not possible — its quantity lives across several
    // component lines that only the bundle app knows how to adjust.
    bar.toggleAttribute('data-pl-opaque', opaqueAdded && n === 0);

    if (soldOutBtn) soldOutBtn.hidden = !unavailable;
    if (addBtn) addBtn.hidden = unavailable || inCart;
    if (cartBox) cartBox.hidden = unavailable || !inCart;
  }

  // Single place that turns a cart payload into bar state.
  function syncFrom(cart) {
    var n = readCount(cart);
    var total = typeof cart.item_count === 'number' ? cart.item_count : cartTotal;

    if (n === 0 && total > cartTotal) opaqueAdded = true;
    if (total === 0) opaqueAdded = false;

    cartTotal = total;
    desired = n;
    render(n);
    renderMini(cart);
  }

  /*
    Reconciliation that cannot move backwards against an add still in flight.

    The add is handed to the product form, whose POST also renders theme
    sections and runs the shop's app scripts — routinely slower than the first
    reconciliation pull below. A /cart.js read that lands before that POST
    commits reports the PREVIOUS quantity, and applying it repainted the bar
    all the way back to "Add to cart" until the next pull corrected it. That
    was the flicker.

    So while an add is outstanding, a payload counts only if it shows the add
    actually arrived. `item_count` carries the bundle case, where the bundle's
    own variant never appears in the cart but the totals still rise.

    `force` is passed on the final pull so a genuinely failed or silently
    dropped add still settles on the truth rather than lying indefinitely.
  */
  function reconcile(cart, force) {
    if (pendingAdd && !force) {
      var total = typeof cart.item_count === 'number' ? cart.item_count : cartTotal;
      var landed = readCount(cart) >= pendingFloor || total > pendingTotal;
      if (!landed) return; // still in flight — keep the optimistic bar
    }
    pendingAdd = false;
    syncFrom(cart);
  }

  /* ---------- cart preview circles ----------

     A stack of round product thumbnails showing what is already in the cart,
     collapsing to "+N" past MINI_MAX. Grouped by PRODUCT, not line item, so
     two variants of the same thing are one circle — it is a list of products,
     not of lines.

     Purely decorative: the group is aria-hidden and holds nothing focusable,
     because the "Your cart" button beside it already carries both the action
     and the accessible name. A second link to the same place would just be
     noise to a screen reader. */

  var MINI_MAX = 3;

  function sized(url) {
    if (!url) return '';
    if (url.indexOf('width=') !== -1) return url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=120';
  }

  function updateMore(distinct, shown) {
    var more = miniEl.querySelector('[data-pl-mini-more]');
    var extra = distinct - shown;
    if (extra > 0) {
      if (!more) {
        more = document.createElement('span');
        more.className = 'pl-sticky-atc__mini-more';
        more.setAttribute('data-pl-mini-more', '');
        miniEl.appendChild(more);
      }
      more.textContent = '+' + extra;
    } else if (more) {
      more.remove();
    }
  }

  function renderMini(cart) {
    if (!miniEl) return;

    var items = (cart && cart.items) || [];
    var seen = {};
    var picks = [];
    var distinct = 0;

    for (var i = 0; i < items.length; i++) {
      var pid = items[i].product_id;
      if (pid == null || seen[pid]) continue;
      seen[pid] = true;
      distinct++;

      var url = items[i].image || (items[i].featured_image && items[i].featured_image.url);
      // A product with no image still counts toward "+N"; it just cannot
      // supply a circle.
      if (url && picks.length < MINI_MAX) picks.push({ pid: pid, url: url });
    }

    var html = '';
    for (var j = 0; j < picks.length; j++) {
      html +=
        '<img class="pl-sticky-atc__mini-img" data-pid="' +
        picks[j].pid +
        '" src="' +
        sized(picks[j].url) +
        '" alt="" width="60" height="60" loading="lazy">';
    }
    miniEl.innerHTML = html;
    miniEl.setAttribute('data-distinct', String(distinct));
    updateMore(distinct, picks.length);
    miniEl.hidden = distinct === 0;
  }

  // The product form's success payload is a single line item, not a cart, so
  // there is nothing to rebuild from. Show this product's own thumbnail right
  // away and let the next full pull correct the grouping.
  function miniAddSelf() {
    if (!miniEl) return false;

    var pid = bar.getAttribute('data-product-id');
    if (!pid || miniEl.querySelector('[data-pid="' + pid + '"]')) return false;

    var distinct = (parseInt(miniEl.getAttribute('data-distinct'), 10) || 0) + 1;
    miniEl.setAttribute('data-distinct', String(distinct));

    var url = bar.getAttribute('data-product-image');
    if (url && miniEl.querySelectorAll('img').length < MINI_MAX) {
      var img = document.createElement('img');
      img.className = 'pl-sticky-atc__mini-img';
      img.setAttribute('data-pid', pid);
      img.src = sized(url);
      img.alt = '';
      img.width = 60;
      img.height = 60;
      miniEl.insertBefore(img, miniEl.querySelector('[data-pl-mini-more]'));
    }

    updateMore(distinct, miniEl.querySelectorAll('img').length);
    miniEl.hidden = false;
    return true;
  }

  // Undo of the above, for an add the server then rejected — otherwise the
  // preview would show a product that is not in the cart until the next pull.
  function miniRemoveSelf() {
    if (!miniEl) return;

    var pid = bar.getAttribute('data-product-id');
    var img = pid && miniEl.querySelector('img[data-pid="' + pid + '"]');
    if (img) img.remove();

    var distinct = Math.max(0, (parseInt(miniEl.getAttribute('data-distinct'), 10) || 0) - 1);
    miniEl.setAttribute('data-distinct', String(distinct));
    updateMore(distinct, miniEl.querySelectorAll('img').length);
    miniEl.hidden = distinct === 0;
  }

  // Matches the ₹, no-paise format that price.liquid renders server-side.
  // en-IN so grouping stays lakh-style (1,00,000) rather than 100,000.
  var money = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

  function formatPrice(cents) {
    return '₹' + money.format(Math.floor(cents / 100));
  }

  // Price, compare-at and availability are all per-variant.
  function applyVariant(variant) {
    if (!variant) return;

    bar.setAttribute('data-variant-id', String(variant.id));

    if (variant.available === false) bar.setAttribute('data-unavailable', 'true');
    else bar.removeAttribute('data-unavailable');

    if (priceEl && typeof variant.price === 'number') {
      priceEl.textContent = formatPrice(variant.price);
    }

    if (compareEl) {
      var was = variant.compare_at_price;
      var onSale = typeof was === 'number' && was > variant.price;
      compareEl.hidden = !onSale;
      if (onSale) compareEl.textContent = formatPrice(was);
    }
  }

  /* ---------- cart ---------- */

  function readCount(cart) {
    var id = variantId();
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (items[i].id === id) return items[i].quantity;
    }
    return 0;
  }

  function announce(cart) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pl-sticky-atc', cartData: cart });
    }
  }

  // Surfaces a cart rejection instead of letting the count snap back silently.
  function fail(message) {
    var el = bar.querySelector('[data-pl-error]');
    if (!el) {
      el = document.createElement('p');
      el.className = 'pl-sticky-atc__error';
      el.setAttribute('data-pl-error', '');
      el.setAttribute('role', 'alert');
      bar.insertBefore(el, bar.firstChild);
    }
    el.textContent = message || 'Sorry, this could not be added to your cart.';
    clearTimeout(fail.timer);
    fail.timer = setTimeout(function () {
      el.remove();
    }, 6000);
  }

  function pull(apply) {
    fetch(root() + 'cart.js')
      .then(function (response) {
        return response.json();
      })
      .then(apply)
      .catch(function () {});
  }

  function flush() {
    flushTimer = null;

    // A write is already running; wait for it rather than racing it.
    if (inFlight) {
      schedule();
      return;
    }

    var updates = {};
    updates[variantId()] = desired;
    inFlight = true;

    fetch(root() + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates }),
    })
      .then(function (response) {
        return response.json().then(function (payload) {
          return { ok: response.ok, payload: payload };
        });
      })
      .then(function (result) {
        // Shopify rejects some products outright (bundles return 422, as do
        // stock and quantity-rule violations). Without this the optimistic
        // count would silently snap back with no explanation.
        if (!result.ok) {
          fail(result.payload && (result.payload.description || result.payload.message));
          pull(function (cart) {
            desired = readCount(cart);
            render(desired);
          });
          return;
        }

        var cart = result.payload;

        // Only reconcile if the shopper has stopped tapping — otherwise this
        // stale response would overwrite input they have already made.
        if (!flushTimer) {
          var server = readCount(cart);
          if (server !== desired) {
            desired = server;
            render(server);
          }
        }
        announce(cart);
      })
      .catch(function () {
        // Roll back to whatever the cart really says.
        if (!flushTimer) {
          pull(function (cart) {
            desired = readCount(cart);
            render(desired);
          });
        }
      })
      .then(function () {
        inFlight = false;
      });
  }

  function schedule() {
    if (flushTimer) clearTimeout(flushTimer);
    flushTimer = setTimeout(flush, DEBOUNCE_MS);
  }

  function setDesired(next) {
    desired = Math.max(0, next);
    render(desired); // instant — the request catches up behind it
    schedule();
  }

  /* ---------- events ---------- */

  /* ---------- the first add goes through the real product form ----------

     Critically NOT the Cart API. Apps hook the theme's form, not the endpoint:
     bundle apps intercept the submit to expand a bundle into its component
     line items (Shopify rejects a bundle variant outright otherwise), and
     volume-tier widgets like "Buy more, Save more" write the chosen quantity
     or variant into that same form. Posting to the Cart API skips all of it,
     which is why bundles 422'd and tier selections added a single unit.

     Clicking the form's own submit button — rather than form.requestSubmit() —
     fires any click handlers an app has bound to the button as well as the
     form's submit event, so both integration styles are covered.
     ---------------------------------------------------------------------- */

  function nativeSubmit() {
    var form = document.querySelector(
      'product-info product-form form[data-type="add-to-cart-form"]'
    );
    if (!form) return null;
    return form.querySelector('[type="submit"]:not([disabled])');
  }

  function addViaForm() {
    var submit = nativeSubmit();
    if (!submit) return false;

    // Repaint to the in-cart state straight away. The delegated submit is a
    // real server round trip — Dawn's add request also renders the cart
    // notification section, and the shop's app scripts add more on top — so
    // waiting for it is what made the stepper take seconds to appear.
    // The reconciliation below corrects the number, and a rejected add rolls
    // back and shows the reason.
    var form = submit.form;
    var qtyField = form && form.querySelector('[name="quantity"]');
    var adding = qtyField ? parseInt(qtyField.value, 10) || 1 : 1;
    desired = desired + adding;

    // Mark the add outstanding BEFORE the click, so a pull that beats the
    // POST cannot undo the repaint below.
    pendingFloor = desired;
    pendingTotal = cartTotal;
    pendingBefore = desired - adding;
    pendingAdd = true;

    render(desired);
    pendingMini = miniAddSelf();

    submit.click();

    // Dawn publishes cartUpdate the moment its AJAX add resolves and the
    // subscriber below repaints immediately — that is the fast path.
    //
    // An app that adds through its own request may publish nothing at all, so
    // re-check on a short ladder rather than one long wait. The first check is
    // quick enough to feel instant when the fast path did not fire; the later
    // ones cover slower app round-trips.
    // The last step forces the server's answer through even if the add never
    // visibly landed, so a silently dropped add cannot leave the bar lying
    // forever. It sits far out because a REJECTED add is already handled
    // promptly by the cartError subscriber below — this only has to catch
    // failures that announce nothing, and a slow-but-successful add must not
    // trip it. At 2.5s it did: a 3.2s add flickered back to "Add to cart".
    var ladder = [400, 1200, 2500, 5000, 9000];
    ladder.forEach(function (delay, index) {
      var last = index === ladder.length - 1;
      setTimeout(function () {
        if (flushTimer || inFlight) return;
        pull(function (cart) {
          reconcile(cart, last);
        });
      }, delay);
    });

    return true;
  }

  bar.addEventListener('click', function (event) {
    if (event.target.closest('[data-pl-add]')) {
      // Fall back to the API only if the page has no usable form.
      if (!addViaForm()) setDesired(desired + 1);
      return;
    }

    // Steps adjust a line that already exists, so the API is correct here and
    // keeps the stepper instant.
    var step = event.target.closest('[data-pl-step]');
    if (step) {
      setDesired(desired + (parseInt(step.getAttribute('data-pl-step'), 10) || 0));
    }
  });

  // Leaving the page with a debounced write still queued would silently drop
  // it, so send it immediately on the way out.
  window.addEventListener('pagehide', function () {
    if (flushTimer) {
      clearTimeout(flushTimer);
      flushTimer = null;
      var updates = {};
      updates[variantId()] = desired;
      var body = JSON.stringify({ updates: updates });
      if (navigator.sendBeacon) {
        navigator.sendBeacon(root() + 'cart/update.js', new Blob([body], { type: 'application/json' }));
      }
    }
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    // Someone else changed the cart (cart drawer, another form, quick add).
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (event && event.source === 'pl-sticky-atc') return; // our own write
      if (flushTimer || inFlight) return; // our pending intent wins

      // /cart/add.js returns the affected line item, and its quantity is the
      // new cumulative quantity for that variant. When the event carries that,
      // repaint from it directly rather than spending another round trip on
      // /cart.js.
      var data = event && event.cartData;
      if (data && data.id === variantId() && typeof data.quantity === 'number') {
        desired = data.quantity;
        opaqueAdded = false;
        pendingAdd = false; // the add landed — this IS the confirmation
        render(desired);
        // The payload is one line item, so the circles still need a real cart.
        pull(renderMini);
        return;
      }

      pull(function (cart) {
        reconcile(cart);
      });
    });

    // product-form.js publishes this when Shopify rejects the add (sold out,
    // quantity rules, a bundle it cannot expand). Without it the bar sat on
    // its optimistic count until the last ladder step 2.5s later.
    subscribe(PUB_SUB_EVENTS.cartError, function (event) {
      if (!event || event.source !== 'product-form') return;
      if (!pendingAdd) return;

      pendingAdd = false;

      // Undo the optimistic repaint from the remembered pre-add count rather
      // than waiting on /cart.js — the shopper gets the button back with the
      // reason in the same frame as the rejection. The pull behind it still
      // reconciles, so this staying wrong is not a risk.
      desired = pendingBefore;
      render(desired);
      if (pendingMini) miniRemoveSelf();
      pendingMini = false;

      var reason = event.errors || event.message;
      fail(typeof reason === 'string' ? reason : null);
      pull(syncFrom);
    });

    // Price, compare-at, availability and cart quantity are all per-variant.
    subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
      var variant = event && event.data && event.data.variant;
      if (!variant) return;

      applyVariant(variant);
      opaqueAdded = false; // a different variant, so any bundle add no longer applies
      pendingAdd = false; // and any add outstanding was for the previous one
      pull(syncFrom);
    });
  }

  /* ---------- delivery line and cutoff countdown ----------

     Both live in pujalane-delivery-eta.js, shared with the cart page's sticky
     bar so the two never quote different dates. It fills this bar's
     [data-pl-delivery-text] and [data-pl-cutoff] elements. */

  /* ---------- layout ---------- */

  // The bottom nav is an app block of unknown height, so the bar is lifted
  // clear of it by measuring rather than by a hard-coded offset. The same
  // value pads the page so the bar never covers the end of the content.
  function measure() {
    // .pl-bottomnav is the theme's own nav; the [data-nav-item] fallback keeps
    // this working if an app-rendered nav is ever used again, where the
    // attribute sits on the links and the bar itself is their wrapper.
    var nav = document.querySelector('.pl-bottomnav');
    if (!nav) {
      var link = document.querySelector('[data-nav-item]');
      nav = link ? link.closest('div') : null;
    }
    var navHeight = nav ? nav.getBoundingClientRect().height : 0;
    document.documentElement.style.setProperty('--pl-stickyatc-navgap', navHeight + 'px');
    document.documentElement.style.setProperty(
      '--pl-stickyatc-height',
      bar.getBoundingClientRect().height + 'px'
    );
  }

  measure();
  window.addEventListener('resize', measure);
  window.addEventListener('load', measure);

  render(desired);
})();
